import { useEffect, useState } from "react";

/** Less than this and it is browser chrome moving, not a keyboard. */
const KEYBOARD_MIN_PX = 120;

const isEditable = (element: Element | null): boolean => {
  if (!element) return false;
  if (element instanceof HTMLTextAreaElement) return !element.readOnly;
  if (element instanceof HTMLInputElement) {
    return !element.readOnly && !["button", "checkbox", "radio", "range", "submit", "reset", "file", "color"].includes(element.type);
  }
  return element instanceof HTMLElement && element.isContentEditable;
};

/**
 * Whether the on-screen keyboard is up, and how much of the page it covers.
 *
 * The overlap is written to `--kb` on the root element, because the two browsers
 * disagree about what a keyboard does to the page. Android (with
 * `interactive-widget=resizes-content`) shrinks the layout viewport, so `dvh`
 * already fits and the overlap comes out 0. iOS leaves the layout viewport alone,
 * lays the keyboard over it and pans the page to reach the input — which is the
 * jump. There the app, every sheet and every dialog subtract `--kb` from their
 * height, and the pan is undone, so a composer sits right on top of the keyboard.
 *
 * "Up" is focus plus a shrunken visual viewport, measured against the tallest
 * viewport seen without an input focused: a focused field on a desktop, or a
 * hardware keyboard on an iPad, shrinks nothing and so hides nothing.
 */
export function useKeyboard(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const root = document.documentElement;
    let baseline = viewport.height;
    let frame = 0;

    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const editing = isEditable(document.activeElement);
        if (!editing) baseline = Math.max(baseline, viewport.height);
        const up = editing && baseline - viewport.height > KEYBOARD_MIN_PX;
        const overlap = up ? Math.max(0, Math.round(window.innerHeight - viewport.height)) : 0;
        root.style.setProperty("--kb", `${overlap}px`);
        // Undo iOS's pan: the app has already shrunk to the visible part.
        if (up && overlap > 0 && (window.scrollY !== 0 || viewport.offsetTop !== 0)) window.scrollTo(0, 0);
        // And the pan iOS can leave behind once the keyboard has gone.
        if (!up && window.scrollY !== 0) window.scrollTo(0, 0);
        setOpen(up);
      });
    };
    // A rotation changes the tallest viewport there is.
    const reset = () => {
      baseline = 0;
      update();
    };

    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    document.addEventListener("focusin", update);
    // After a tick, so moving focus between two fields does not flash the tab bar.
    const blur = () => window.setTimeout(update, 50);
    document.addEventListener("focusout", blur);
    window.addEventListener("orientationchange", reset);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", blur);
      window.removeEventListener("orientationchange", reset);
      root.style.removeProperty("--kb");
    };
  }, []);

  return open;
}
